import React, { useReducer } from "react";
import axiosInstance from "../../utilities/Axios";

type User = {
  id: number;
  role: string;
  username: string;
  remember_token: null;
  created_at: string;
  updated_at: string;
  permissions: {
    [key: string]: string[];
  };
};

type IAuth = {
  auth: {
    access_token: string;
    user: User;
    role: string;
    is_logged_in: boolean;
  } | null;
  login: any;
  logout: any;
  init: any;
  hasPermission: (module: string, permissions: string[]) => boolean;
};

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case "login":
      return action.payload;
    case "logout":
      return null;
  }
  return state;
};

export const AuthContext = React.createContext<IAuth>({
  auth: null,
  login: (user: User) => { },
  logout: () => { },
  init: () => { },
  hasPermission: (module: string, permissions: string[]) => false,
});

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [authState, dispatch] = useReducer(reducer, null);

  AuthContext.displayName = "Auth Context";

  const login = async (data: any) => {
    const userData = data.user || data.agent || data.staff;
    const token = data.access_token?.token || data.access_token;
    const role = userData?.role || data.role || (data.staff ? "staff" : "agent");

    localStorage.setItem("token", token);
    localStorage.setItem("role", role);

    axiosInstance.defaults.headers.common["Authorization"] = "Bearer " + token;

    dispatch({
      type: "login",
      payload: {
        user: userData,
        access_token: token,
        role: role,
        permissions: data.permissions || {},
        is_logged_in: true,
      },
    });
  };

  const logout = () => {
    const storedRole = localStorage.getItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("role");

    axiosInstance.defaults.headers.common["Authorization"] = "";

    dispatch({ type: "logout" });

    if (!window.location.pathname.includes("/login")) {
      const isAgentPortal = window.location.pathname.includes("/agent");
      window.location.href = isAgentPortal ? "/agent/login" : storedRole === "staff" ? "/staff/login" : "/login";
    }
  };

  const init = async () => {
    try {
      const token = localStorage.getItem("token");
      const stored_role = localStorage.getItem("role");

      if (token) {
        axiosInstance.defaults.headers.common["Authorization"] = "Bearer " + token;

        let res;
        if (stored_role === "agent") {
          res = await axiosInstance.get("/v1/agents/auth/me");
        } else if (stored_role === "staff") {
          res = await axiosInstance.get("/v1/staffs/auth/me");
        } else {
          res = await axiosInstance.post("/v1/account/me");
        }

        const result = res.data.result;
        const userData = result.user || result.agent || result.staff || result;
        const role = userData.role || stored_role || (result.agent ? "agent" : result.staff ? "staff" : "staff");

        const payload = {
          user: userData,
          token: token,
          role: role,
          permissions: result.permissions || {},
          is_logged_in: true,
          access_token: token
        };

        if (window.location.pathname === "" || window.location.pathname === "/") {
          window.location.href = role === "chairman" ? "/chairman/dashboard" : role === "agent" ? "/admin/agent-dashboard" : role === "staff" ? "/staff/payslips" : "/admin";
        }
        dispatch({ type: "login", payload: payload });
      }
    } catch (err: any) {
      console.error("Auth Init Error:", err);
      // Only logout if we are not on a login / password-recovery page to avoid loops
      const publicAuthPaths = ["/login", "/agent/forgot-password", "/agent/reset-password"];
      if (!publicAuthPaths.some((p) => window.location.pathname.includes(p))) {
        logout();
      }
    }
  };

  const hasPermission = (module: string, permissions: string[]) => {
    if (!authState || !authState.permissions) {
      return false;
    }

    if (authState.permissions['ALL']) {
      const userPermissions = authState.permissions['ALL'];
      if (userPermissions.includes('ANY')) {
        return true;
      }
      return permissions.every((perm) => userPermissions.includes(perm));
    }

    const userPermissions = authState.permissions[module] || [];
    return permissions.every((perm) => userPermissions.includes(perm));
  };

  return (
    <AuthContext.Provider
      value={{
        auth: authState,
        login: login,
        logout: logout,
        init: init,
        hasPermission: hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
